import { Plus } from "lucide-react";
import React, { useCallback, useEffect, useState, useTransition } from "react";
import { graphql, useMutation, useRefetchableFragment } from "react-relay";
import { useNavigate, useSearchParams } from "react-router";
import { getIntegrationCategories } from "src/components/integrations/integration-categories";
import IntegrationCategoryCard from "src/components/integrations/IntegrationCategoryCard";
import { Button } from "src/components/ui/button";
import { usePermissions } from "src/context/MeContext";
import { useToast } from "src/hooks/use-toast";
import AddKnowledgeResourceModal from "src/pages/KnowledgePage/components/AddKnowledgeResourceModal";
import type { GenerationStatusType } from "src/types/onboarding-settings";
import { getProductName } from "src/utils/brandTheme";

import type { CompleteOnboardingMutation as CompleteOnboardingMutationType } from "../mutations/__generated__/CompleteOnboardingMutation.graphql";
import type { CreateInstructionGenerationRequestMutation as CreateInstructionGenerationRequestMutationType } from "../mutations/__generated__/CreateInstructionGenerationRequestMutation.graphql";
import type { CreateTeamMutation as CreateTeamMutationType } from "../mutations/__generated__/CreateTeamMutation.graphql";
import type { UpdateInstructionMutation as UpdateInstructionMutationType } from "../mutations/__generated__/UpdateInstructionMutation.graphql";
import { CompleteOnboardingMutation } from "../mutations/CompleteOnboardingMutation";
import { CreateInstructionGenerationRequestMutation } from "../mutations/CreateInstructionGenerationRequestMutation";
import { CreateTeamMutation } from "../mutations/CreateTeamMutation";
import { UpdateInstructionMutation } from "../mutations/UpdateInstructionMutation";
import type { OnboardingContent_organization$key } from "./__generated__/OnboardingContent_organization.graphql";
import {
  clearCompanyContextFromStorage,
  CompanyContextStep,
  loadCompanyContextFromStorage,
} from "./CompanyContextStep";
import { OnboardingWizard } from "./OnboardingWizard";
import { ReviewStep } from "./ReviewStep";
import { TeamStep } from "./TeamStep";
import { WelcomeStep } from "./WelcomeStep";

const onboardingContentFragment = graphql`
  fragment OnboardingContent_organization on GraphQLOrganization
  @refetchable(queryName: "OnboardingContentRefetchQuery") {
    id
    brandTheme
    isCodeAccessOptional
    onboardingCompletedAt
    onboardingInitializationStatus
    teams {
      edges {
        node {
          id
          name
        }
      }
    }
    integrations {
      edges {
        node {
          id
          integrationType
        }
      }
    }
    latestInstruction {
      id
      body
    }
    activeCustomMcpServers {
      name
    }
  }
`;

interface OnboardingContentProps {
  organization: OnboardingContent_organization$key;
}

interface CompanyContextData {
  products: string;
  techStack: string;
}

// Map server status to local generation status
function mapServerStatusToGenerationStatusType(
  serverStatus: string | undefined
): GenerationStatusType {
  switch (serverStatus) {
    case "IN_PROGRESS":
      return "in_progress";
    case "COMPLETED":
      return "completed";
    case "FAILED":
      return "failed";
    case "NOT_STARTED":
    default:
      return "pending";
  }
}

export default function OnboardingContent({ organization: organizationKey }: OnboardingContentProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const [organization, refetch] = useRefetchableFragment(onboardingContentFragment, organizationKey);

  const brandTheme = organization.brandTheme;
  const productName = getProductName(brandTheme);
  const organizationId = organization.id;
  const [, startTransition] = useTransition();

  // Track highest step reached for navigation (persisted per org)
  // eslint-disable-next-line custom/no-hardcoded-brand-name -- localStorage key
  const highestStepStorageKey = `tierzero_onboarding_highest_step_${organizationId}`;

  // Derive minimum step from server state as a fallback for localStorage
  const serverMinStep = (() => {
    const initStatus = organization.onboardingInitializationStatus;
    if (initStatus === "IN_PROGRESS" || initStatus === "COMPLETED" || initStatus === "FAILED") {
      return 5;
    }
    const hasIntegrations = (organization.integrations?.edges ?? []).length > 0;
    if (hasIntegrations) return 1;
    return 0;
  })();

  // Calculate initial step: use URL param if provided, otherwise use stored highest step
  const urlStep = searchParams.get("step");
  const [currentStep, setCurrentStep] = useState(() => {
    // If URL has explicit step, use it
    if (urlStep !== null) {
      return parseInt(urlStep, 10);
    }
    // Resume from highest step reached (max of localStorage and server-derived minimum)
    try {
      const stored = localStorage.getItem(highestStepStorageKey);
      const storedStep = stored ? parseInt(stored, 10) : 0;
      return Math.max(storedStep, serverMinStep);
    } catch {
      return serverMinStep;
    }
  });

  const [isResourceModalOpen, setIsResourceModalOpen] = useState(false);

  const [highestStepReached, setHighestStepReached] = useState(() => {
    try {
      const stored = localStorage.getItem(highestStepStorageKey);
      const storedStep = stored ? parseInt(stored, 10) : 0;
      return Math.max(storedStep, currentStep, serverMinStep);
    } catch {
      return Math.max(currentStep, serverMinStep);
    }
  });

  // Persist step to localStorage synchronously when advancing
  const persistStep = useCallback((step: number) => {
    try {
      const stored = localStorage.getItem(highestStepStorageKey);
      const currentHighest = stored ? parseInt(stored, 10) : 0;
      if (step > currentHighest) {
        localStorage.setItem(highestStepStorageKey, String(step));
      }
    } catch {
      // Ignore localStorage errors
    }
  }, [highestStepStorageKey]);

  // Update highest step when current step advances
  useEffect(() => {
    if (currentStep > highestStepReached) {
      setHighestStepReached(currentStep);
      persistStep(currentStep);
    }
  }, [currentStep, highestStepReached, persistStep]);

  // Data collected during wizard - initialize from localStorage if available
  const [companyContextData, setCompanyContextData] = useState<CompanyContextData | null>(() =>
    loadCompanyContextFromStorage(organizationId)
  );
  // Track created team ID for potential future use (e.g., navigation after onboarding)
  const [, setCreatedTeamId] = useState<string | null>(null);

  // Generation status for review step - initialize from server status
  const serverInitStatus = organization.onboardingInitializationStatus;
  const [generationStatus, setGenerationStatusType] = useState<GenerationStatusType>(() =>
    mapServerStatusToGenerationStatusType(serverInitStatus)
  );
  // Initialize instruction content from server if already completed
  const [generatedInstruction, setGeneratedInstruction] = useState<string | null>(() =>
    serverInitStatus === "COMPLETED" ? organization.latestInstruction?.body ?? null : null
  );

  // Mutations
  const [commitCreateTeam, isCreatingTeam] = useMutation<CreateTeamMutationType>(CreateTeamMutation);
  const [commitCreateGenRequest, isCreatingGenRequest] =
    useMutation<CreateInstructionGenerationRequestMutationType>(CreateInstructionGenerationRequestMutation);
  const [commitCompleteOnboarding, isCompletingOnboarding] =
    useMutation<CompleteOnboardingMutationType>(CompleteOnboardingMutation);
  const [commitUpdateInstruction, isUpdatingInstruction] =
    useMutation<UpdateInstructionMutationType>(UpdateInstructionMutation);

  // Show cancel button only if onboarding was already completed (user is revisiting)
  const showCancel = !!organization.onboardingCompletedAt;
  const handleCancel = useCallback(() => {
    navigate("/");
  }, [navigate]);

  const connectedTypes = (organization.integrations?.edges ?? [])
    .map(edge => edge?.node?.integrationType)
    .filter(Boolean) as string[];

  // Handle clicking on a completed step to go back
  const handleStepClick = useCallback((step: number) => {
    setCurrentStep(step);
  }, []);

  // Handle step 0 (Welcome) get started
  const handleGetStarted = useCallback(() => {
    persistStep(1);
    setCurrentStep(1);
  }, [persistStep]);

  // Handle step 1 (Integrations) continue
  const handleIntegrationsContinue = useCallback(() => {
    persistStep(2);
    setCurrentStep(2);
  }, [persistStep]);

  // Handle step 2 (Resources) continue
  const handleResourcesContinue = useCallback(() => {
    persistStep(3);
    setCurrentStep(3);
  }, [persistStep]);

  // Handle step 3 (Company Context) continue - just save data and advance
  const handleCompanyContextContinue = useCallback(
    (data: CompanyContextData) => {
      setCompanyContextData(data);
      persistStep(4);
      setCurrentStep(4);
    },
    [persistStep]
  );

  // Handle starting initialization from Review step
  const handleStartInitialization = useCallback(() => {
    commitCreateGenRequest({
      variables: {
        input: {
          instructionType: "ORGANIZATION",
          generationInput: {
            products: companyContextData?.products || "",
            techStack: companyContextData?.techStack || "",
          },
        },
      },
      onCompleted: response => {
        if (response.createInstructionGenerationRequest.success) {
          setGenerationStatusType("in_progress");
        } else {
          toast({
            variant: "error",
            title: "Failed to start initialization",
            description: response.createInstructionGenerationRequest.error || "Unknown error",
          });
        }
      },
      onError: error => {
        toast({
          variant: "error",
          title: "Failed to start initialization",
          description: error.message,
        });
      },
    });
  }, [commitCreateGenRequest, companyContextData, toast]);

  // Handle step 4 (Team) - create team
  const handleCreateTeam = useCallback(
    (data: { name: string; description: string }) => {
      commitCreateTeam({
        variables: {
          input: {
            name: data.name,
            description: data.description,
          },
        },
        onCompleted: response => {
          if (response.createTeam?.team?.id) {
            setCreatedTeamId(response.createTeam.team.id);
            toast({
              variant: "success",
              title: "Team created",
              description: `Team "${data.name}" has been created.`,
            });
            persistStep(5);
            setCurrentStep(5);
            // Refetch to update teams list without triggering Suspense
            startTransition(() => {
              refetch({}, { fetchPolicy: "network-only" });
            });
          }
        },
        onError: error => {
          toast({
            variant: "error",
            title: "Failed to create team",
            description: error.message,
          });
        },
      });
    },
    [commitCreateTeam, persistStep, refetch, startTransition, toast]
  );

  // Handle step 4 (Team) skip - create default "General" team
  const handleSkipTeam = useCallback(() => {
    commitCreateTeam({
      variables: {
        input: {
          name: "General",
          description: "Default team for all users",
        },
      },
      onCompleted: response => {
        if (response.createTeam?.team?.id) {
          setCreatedTeamId(response.createTeam.team.id);
          persistStep(5);
          setCurrentStep(5);
          // Refetch to update teams list without triggering Suspense
          startTransition(() => {
            refetch({}, { fetchPolicy: "network-only" });
          });
        }
      },
      onError: error => {
        toast({
          variant: "error",
          title: "Failed to create default team",
          description: error.message,
        });
        // Still advance to allow completion
        persistStep(5);
        setCurrentStep(5);
      },
    });
  }, [commitCreateTeam, persistStep, refetch, startTransition, toast]);

  // Handle step 4 (Review) confirm - complete onboarding
  const handleReviewConfirm = useCallback(
    (editedContent: string) => {
      // If content was edited, update the instruction first
      const originalContent = generatedInstruction || organization.latestInstruction?.body || "";
      const wasEdited = editedContent !== originalContent;

      const completeOnboarding = () => {
        commitCompleteOnboarding({
          variables: {},
          onCompleted: response => {
            if (response.completeOnboarding.success) {
              // Clear saved onboarding data from localStorage
              clearCompanyContextFromStorage(organizationId);
              try {
                localStorage.removeItem(highestStepStorageKey);
              } catch {
                // Ignore localStorage errors
              }
              toast({
                variant: "success",
                title: "Welcome to " + productName + "!",
                description: "Onboarding complete. Let's start chatting.",
              });
              navigate("/chat");
            } else {
              toast({
                variant: "error",
                title: "Failed to complete onboarding",
                description: response.completeOnboarding.error || "Unknown error",
              });
            }
          },
          onError: error => {
            toast({
              variant: "error",
              title: "Failed to complete onboarding",
              description: error.message,
            });
          },
        });
      };

      if (wasEdited && editedContent.trim()) {
        // Update instruction first, then complete onboarding
        commitUpdateInstruction({
          variables: {
            body: editedContent,
          },
          onCompleted: response => {
            if (response.updateInstruction.error) {
              toast({
                variant: "error",
                title: "Failed to save changes",
                description:
                  response.updateInstruction.error.message +
                  " Please try again.",
              });
              return; // Don't complete onboarding if save failed
            }
            completeOnboarding();
          },
          onError: error => {
            toast({
              variant: "error",
              title: "Failed to save changes",
              description: error.message + " Please try again.",
            });
            // Don't complete onboarding - let user retry
          },
        });
      } else {
        completeOnboarding();
      }
    },
    [
      commitCompleteOnboarding,
      commitUpdateInstruction,
      generatedInstruction,
      highestStepStorageKey,
      organization.latestInstruction?.body,
      organizationId,
      navigate,
      productName,
      toast,
    ]
  );

  // Sync local state with server status when it changes (e.g., page refresh, data update)
  useEffect(() => {
    // If server says completed but we think it's still in progress, update local state
    if (serverInitStatus === "COMPLETED" && generationStatus === "in_progress") {
      setGenerationStatusType("completed");
      if (organization.latestInstruction?.body) {
        setGeneratedInstruction(organization.latestInstruction.body);
      }
    }
    // If server says failed but we think it's still in progress
    else if (serverInitStatus === "FAILED" && generationStatus === "in_progress") {
      setGenerationStatusType("failed");
    }
    // If server says in_progress but we think it's pending (another session started it)
    else if (serverInitStatus === "IN_PROGRESS" && generationStatus === "pending") {
      setGenerationStatusType("in_progress");
    }
  }, [serverInitStatus, generationStatus, organization.latestInstruction?.body]);

  // Poll for generation completion when initialization has been started
  useEffect(() => {
    // Only poll when status is "in_progress" (user clicked Start Initialization)
    if (currentStep !== 5 || generationStatus !== "in_progress") {
      return;
    }

    // Use startTransition for polling to keep old UI visible while fetching
    const doRefetch = () => {
      startTransition(() => {
        refetch({}, { fetchPolicy: "network-only" });
      });
    };

    // Poll the server every 3 seconds for status updates
    const pollInterval = setInterval(doRefetch, 3000);

    // Also do an immediate refetch
    doRefetch();

    return () => clearInterval(pollInterval);
  }, [currentStep, generationStatus, refetch, startTransition]);

  // Map integrations for AddKnowledgeResourceModal - use actual integration objects with IDs
  const integrationsList = (organization.integrations?.edges ?? [])
    .filter(edge => edge?.node)
    .map(edge => ({
      id: edge!.node!.id,
      name: edge!.node!.integrationType || "",
      type: edge!.node!.integrationType || "",
      status: "connected" as const,
    }));

  return (
    <OnboardingWizard currentStep={currentStep} highestStepReached={highestStepReached} onStepClick={handleStepClick} brandTheme={brandTheme}>
      {currentStep === 0 && <WelcomeStep onGetStarted={handleGetStarted} brandTheme={brandTheme} />}

      {currentStep === 1 && (
        <IntegrationsStep
          productName={productName}
          brandTheme={brandTheme}
          connectedTypes={connectedTypes}
          mcpServerCount={organization.activeCustomMcpServers?.length ?? 0}
          isCodeAccessOptional={organization.isCodeAccessOptional}
          onContinue={handleIntegrationsContinue}
          onCancel={showCancel ? handleCancel : undefined}
        />
      )}

      {currentStep === 2 && (
        <ResourcesStep
          productName={productName}
          integrations={integrationsList}
          isModalOpen={isResourceModalOpen}
          setIsModalOpen={setIsResourceModalOpen}
          onContinue={handleResourcesContinue}
          onCancel={showCancel ? handleCancel : undefined}
        />
      )}

      {currentStep === 3 && (
        <CompanyContextStep
          organizationId={organization.id}
          brandTheme={brandTheme}
          onContinue={handleCompanyContextContinue}
          isLoading={isCreatingGenRequest}
          isInitializationStarted={generationStatus !== "pending"}
          productName={productName}
          onCancel={showCancel ? handleCancel : undefined}
        />
      )}

      {currentStep === 4 && (
        <TeamStep
          onCreateTeam={handleCreateTeam}
          onSkip={handleSkipTeam}
          onContinue={() => { persistStep(5); setCurrentStep(5); }}
          isLoading={isCreatingTeam}
          productName={productName}
          existingTeams={(organization.teams?.edges ?? [])
            .filter(edge => edge?.node)
            .map(edge => ({ id: edge!.node!.id, name: edge!.node!.name }))}
          onCancel={showCancel ? handleCancel : undefined}
        />
      )}

      {currentStep === 5 && (
        <ReviewStep
          generationStatus={generationStatus}
          instructionContent={generatedInstruction || ""}
          onStartInitialization={handleStartInitialization}
          onConfirm={handleReviewConfirm}
          isStartingInitialization={isCreatingGenRequest}
          isLoading={isCompletingOnboarding || isUpdatingInstruction}
          productName={productName}
          brandTheme={brandTheme}
          onCancel={showCancel ? handleCancel : undefined}
        />
      )}
    </OnboardingWizard>
  );
}

// Step 0: Integrations
interface IntegrationsStepProps {
  productName: string;
  brandTheme?: string | null;
  connectedTypes: string[];
  mcpServerCount: number;
  isCodeAccessOptional: boolean;
  onContinue: () => void;
  onCancel?: () => void;
}

const IntegrationsStep = React.memo<IntegrationsStepProps>(function IntegrationsStep({
  productName,
  brandTheme,
  connectedTypes,
  mcpServerCount,
  isCodeAccessOptional,
  onContinue,
  onCancel,
}) {
  const { isSuperAdmin } = usePermissions();
  const categories = getIntegrationCategories(productName, brandTheme, isSuperAdmin);

  // Check required integrations using existing category structure
  const telemetryCategory = categories.find(c => c.id === "telemetry");
  const githubCategory = categories.find(c => c.id === "source-control");

  const hasObservability = telemetryCategory?.integrationTypes.some(type =>
    connectedTypes.includes(type)
  ) ?? false;
  const hasGitHub = githubCategory?.integrationTypes.some(type =>
    connectedTypes.includes(type)
  ) ?? false;

  const canContinue = hasObservability && (hasGitHub || isCodeAccessOptional);

  // Build missing requirements message
  const missingItems: string[] = [];
  if (!hasObservability) missingItems.push("an observability source");
  if (!hasGitHub && !isCodeAccessOptional) missingItems.push("a source control integration");

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-foreground">Connect Your Integrations</h2>
        <p className="text-muted-foreground mt-2">
          Connect {productName} to your systems so it can investigate issues and perform tasks.
          You can always add more integrations later.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {categories.map(category => (
          <IntegrationCategoryCard
            key={category.id}
            category={category}
            connectedTypes={connectedTypes}
            isRequired={category.id === "telemetry" || (category.id === "source-control" && !isCodeAccessOptional)}
            {...(category.id === "mcp-servers" && mcpServerCount > 0 ? {
              connectedCountOverride: mcpServerCount,
              connectedLabel: `${mcpServerCount} server${mcpServerCount !== 1 ? "s" : ""} connected`,
            } : {})}
          />
        ))}
      </div>

      <div className="flex items-center justify-between pt-4">
        <div className="flex items-center gap-2">
          {onCancel && (
            <Button variant="ghost" onClick={onCancel}>
              Cancel
            </Button>
          )}
          {!canContinue && (
            <span className="text-muted-foreground text-sm">
              Connect {missingItems.join(" and ")} to continue
            </span>
          )}
        </div>
        <Button onClick={onContinue} disabled={!canContinue}>
          Continue
        </Button>
      </div>
    </div>
  );
});

// Step 1: Resources
interface ResourcesStepProps {
  productName: string;
  integrations: Array<{
    id: string;
    name: string;
    type: string;
    status: "connected";
  }>;
  isModalOpen: boolean;
  setIsModalOpen: (open: boolean) => void;
  onContinue: () => void;
  onCancel?: () => void;
}

const ResourcesStep = React.memo<ResourcesStepProps>(function ResourcesStep({
  productName,
  integrations,
  isModalOpen,
  setIsModalOpen,
  onContinue,
  onCancel,
}) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-foreground">Teach {productName} How Your Team Debugs</h2>
        <p className="text-muted-foreground mt-2">
          Share dashboards, runbooks, and docs that give insight into your systems. {productName}{" "}
          learns from these to investigate incidents like another member of your team.
        </p>
      </div>

      {/* What works well - subtle list */}
      <div className="bg-card/30 rounded-lg px-5 py-4">
        <p className="text-muted-foreground text-sm mb-3">Resources that work well:</p>
        <ul className="text-muted-foreground text-sm space-y-1.5">
          <li className="flex items-start gap-2">
            <span className="text-muted-foreground mt-0.5">•</span>
            <span>Service health dashboards</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-muted-foreground mt-0.5">•</span>
            <span>On-call runbooks and escalation procedures</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-muted-foreground mt-0.5">•</span>
            <span>Past postmortems showing how issues were diagnosed</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-muted-foreground mt-0.5">•</span>
            <span>Architecture docs and system diagrams</span>
          </li>
        </ul>
      </div>

      <div className="flex items-center justify-between pt-4">
        <div className="flex items-center gap-2">
          {onCancel && (
            <Button variant="ghost" onClick={onCancel}>
              Cancel
            </Button>
          )}
          <Button variant="outline" onClick={() => setIsModalOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Resource
          </Button>
        </div>

        <div className="flex items-center gap-4">
          <span className="text-muted-foreground text-sm">You can add more later</span>
          <Button onClick={onContinue}>Continue</Button>
        </div>
      </div>

      <AddKnowledgeResourceModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={() => setIsModalOpen(false)}
        integrations={integrations}
      />
    </div>
  );
});
